import { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { AskHelpAssistant } from '@/apis/helpCenterAPI';
import { AiStar } from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import { helpAssistantSuggestions } from '@/common/static_data';
import type { HelpAssistantMessage, HelpAssistantViewProps } from '../../types';
import styles from '../../AppHelpCenter.module.scss';
import HelpAssistantComposer from '../HelpAssistantComposer/HelpAssistantComposer';
import HelpAssistantMessageItem from '../HelpAssistantMessage/HelpAssistantMessage';

// Messages scroll; the composer stays pinned at the bottom (PRD).
const HelpAssistantView = ({
  initialQuestion,
  articleContextId,
  insightChatEnabled,
  onOpenArticle,
  onAskInsightChat,
}: HelpAssistantViewProps) => {
  const { token } = useToken();
  const [messages, setMessages] = useState<HelpAssistantMessage[]>([]);
  const [pending, setPending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  // StrictMode runs effects twice in dev; ask the carried-in question once.
  const askedInitialRef = useRef(false);

  const ask = (question: string) => {
    // Earlier turns go along so follow-ups have context.
    const history = messages.map((item) =>
      item.role === 'user'
        ? { role: item.role, content: item.content }
        : { role: item.role, content: item.response.answer }
    );
    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setPending(true);

    AskHelpAssistant(token, {
      question,
      article_context_id: articleContextId,
      history,
    })
      .then((res) => {
        const response = res?.data?.data?.data;
        if (response) {
          setMessages((prev) => [...prev, { role: 'assistant', response }]);
        }
      })
      .catch(() => message.error('Could not reach the help assistant.'))
      .finally(() => setPending(false));
  };

  useEffect(() => {
    if (!initialQuestion || askedInitialRef.current) return;
    askedInitialRef.current = true;
    ask(initialQuestion);
    // Runs once for the question this view was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, pending]);

  return (
    <div className={styles.assistant}>
      <div className={styles.assistantMessages}>
        {messages.length === 0 && !pending ? (
          <div className={styles.assistantEmpty}>
            <span className={styles.assistantEmptyIcon}>
              <AiStar color='currentColor' size={20} />
            </span>
            <p className={styles.assistantEmptyText}>
              Ask anything about Zoomlogi. Answers come from the help articles,
              and each one links to the article it used.
            </p>
            <div className={styles.assistantSuggestions}>
              {helpAssistantSuggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type='button'
                  className={styles.assistantSuggestion}
                  onClick={() => ask(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((item, index) => {
            // An answer always follows the question it replies to.
            const previous = messages[index - 1];
            return (
              <HelpAssistantMessageItem
                // eslint-disable-next-line react/no-array-index-key -- messages have no id; the list is append-only
                key={index}
                message={item}
                question={
                  previous?.role === 'user' ? previous.content : undefined
                }
                insightChatEnabled={insightChatEnabled}
                onOpenArticle={onOpenArticle}
                onAskInsightChat={onAskInsightChat}
              />
            );
          })
        )}

        {/* PRD: three pulsing dots while waiting. */}
        {pending && (
          <div
            className={styles.assistantTyping}
            aria-label='Assistant is typing'
          >
            <span />
            <span />
            <span />
          </div>
        )}
        <div ref={endRef} />
      </div>

      <HelpAssistantComposer
        placeholder={messages.length ? 'Ask a follow up' : 'Ask a question'}
        disabled={pending}
        onSend={ask}
      />
    </div>
  );
};

export default HelpAssistantView;
